import { useId, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { useScreenData } from '../hooks/useScreenData';
import RecordList, { ViewSwitch } from '../components/RecordList';
import RecordActions from '../components/RecordActions';
import DetailPage, { RecordFields, orNotSet, status, when } from '../components/DetailPage';

/** Every field the roster returns (platformTenant.service.js getTenant). */
const MEMBER_COLUMNS = [
  ['Member', (u) => <Link to={`/users/${u.id}`}>{u.username}</Link>],
  ['Member ID', (u) => <span className="record-id">{u.id}</span>],
  ['Name', (u) => orNotSet([u.first_name, u.last_name].filter(Boolean).join(' '))],
  ['Display name', (u) => orNotSet(u.display_name)],
  ['Email', (u) => orNotSet(u.email)],
  ['Role', (u) => u.role],
  ['Status', (u) => status(u.is_active)],
  ['Sign-in', (u) => u.auth_provider],
  ['Last sign-in', (u) => when(u.last_login, 'Never')],
  ['Created', (u) => when(u.created_at)],
];

export default function TenantDetail() {
  const { id } = useParams();
  return <TenantRecord key={id} id={id} />;
}

function TenantRecord({ id }) {
  const screen = useScreenData(id, (options) => api.getTenant(id, options));
  const { admin } = useAuth();
  const [tableOnNarrow, setTableOnNarrow] = useState(false);
  const rosterHeading = useId();
  const operator = admin?.displayName || 'you';

  const actionsFor = (t) => {
    const who = `${t.name} (${t.slug})`;
    const common = { refused: (err) => ({
      404: 'this tenant no longer exists.',
      409: `this tenant was already ${t.is_active ? 'locked' : 'unlocked'}. Someone may have changed it first.`,
      400: `the server refused the reason. Write 1 to 500 characters.`,
    })[err.status] };
    return [t.is_active ? {
      ...common, key: 'lock', label: 'Lock Tenant', className: 'btn-danger', field: 'reason', submit: 'Confirm lock',
      title: `Lock tenant ${t.name}`,
      about: `Every member of ${who} cannot sign in until an operator unlocks it. The audit log records this as ${operator}.`,
      send: (reason, options) => api.lockTenant(id, reason.trim(), options),
      done: 'Tenant locked.', unknown: 'the tenant was locked',
    } : {
      ...common, key: 'unlock', label: 'Unlock Tenant', className: 'btn-success', submit: 'Confirm unlock',
      title: `Unlock tenant ${t.name}`,
      // Sign-in needs the member's own account unlocked too (auth.service.js login query).
      about: `This removes the tenant lock for ${who}. A member locked on their own stays locked. Other sign-in requirements still apply. The audit log records this as ${operator}.`,
      send: (_none, options) => api.unlockTenant(id, options),
      done: 'Tenant unlocked.', unknown: 'the tenant was unlocked',
    }];
  };

  return (
    <DetailPage screen={screen} noun="Tenant" back={{ to: '/tenants', label: 'Back to tenants' }} title={(r) => r.tenant.name}
      actions={(record, hold) => <RecordActions actions={record ? actionsFor(record.tenant) : []} disabled={hold}
        refreshing={screen.loading} readSucceeded={Boolean(screen.data) && !screen.stale && !screen.error}
        isFresh={screen.isFresh} onDone={screen.reload} />}>
      {({ tenant: t, users }) => (
        <>
          <RecordFields label="Tenant record" rows={[
            ['Tenant ID', <span className="record-id">{t.id}</span>],
            ['Slug', t.slug],
            ['Plan', <span className="badge badge-plan">{t.plan}</span>],
            ['Status', t.is_active ? status(true) : <>{status(false)} since {when(t.locked_at, 'an unrecorded time')}</>],
            // The server counts only enabled members (u.is_active = true), so the label says so.
            ['Enabled members', <span className="record-count">{t.user_count}</span>],
            ['Member limit', t.max_users == null ? 'Not set' : <span className="record-count">{t.max_users}</span>],
            ['Created', when(t.created_at)],
          ]} />
          <section className="detail-roster" aria-labelledby={rosterHeading}>
            <h3 id={rosterHeading}>Members ({users.length})</h3>
            {users.length === 0 ? <p>No members in this tenant</p> : (
              <>
                <ViewSwitch tableOnNarrow={tableOnNarrow} onChange={setTableOnNarrow} />
                <RecordList tableOnNarrow={tableOnNarrow} label="Members" caption={`Members of ${t.name}, one row each`}
                  columns={MEMBER_COLUMNS} records={users} recordKey={(u) => u.id} />
              </>
            )}
          </section>
        </>
      )}
    </DetailPage>
  );
}
